import { FC, useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import { TFunction, useTranslation } from "next-i18next";

import { FormMessage, FullWidthVStack } from "@components/common";
import Loader from "@components/Loader";
import { OptionButtonGroup } from "@components/OptionButton";
import Select from "@components/Select";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { withCompositeFormikField, TCompositeFormikFieldProps } from "@lib/withFormikField";

import { OrderFields } from "../../constants";
import useOrderData from "../../context/useOrderData";
import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";
import useOrderFormik from "../../hooks/useOrderFormik";
import type { TCountry } from "../../types";
import splitCountriesToDefaultAndOther from "../../utils/splitCountriesToDefaultAndOther";
import SectionTitle from "../common/SectionTitle";

type TCountrySelectProps = {
  name: string;
  label: string;
  value?: string;
  onChange?: (countryCode: string) => void;
} & TCompositeFormikFieldProps;

const OTHER_COUNTRY_VALUE = "other-country";

const getButtonOptions = (t: TFunction, defaultCountry: TCountry | null, otherCountries?: TCountry[]) => {
  const buttonOptions: any[] = [];
  if (defaultCountry) {
    buttonOptions.push({
      label: defaultCountry.title,
      value: defaultCountry.code,
    });
  }
  if (otherCountries?.length) {
    buttonOptions.push({
      label: t("order-package-other_eu_country"),
      value: OTHER_COUNTRY_VALUE,
    });
  }
  return buttonOptions;
};

const CountrySelect: FC<TCountrySelectProps> = ({ name, label, value, onChange, setFieldValue }) => {
  const { t } = useTranslation();

  const isDestinationCountry = name === OrderFields.DESTINATION_COUNTRY;

  const { errors } = useOrderFormik();

  const fieldError = errors[name];

  const {
    staticData: { countriesFrom },
    dynamicData: { countriesTo },
  } = useOrderData();

  const { isFieldLoading } = useOrderFieldsLoading();
  const isLoading = isFieldLoading(name);

  const countries = isDestinationCountry ? countriesTo : countriesFrom;

  const { defaultCountry, otherCountries } = splitCountriesToDefaultAndOther(countries);

  // This state stores selected country, or "OTHER_COUNTRY_VALUE" when other country option is selected,
  // but actual country is not yet selected
  const [selectedOption, setSelectedOption] = useState(value || defaultCountry?.code);
  useEffect(() => {
    setSelectedOption(value || defaultCountry?.code);
  }, [defaultCountry?.code, value]);

  const buttonOptions = getButtonOptions(t, defaultCountry, otherCountries);
  const selectOptions = otherCountries.map((country) => ({ label: country.title, value: country.code }));

  const handleCountryOptionChange = (optionValue) => {
    setSelectedOption(optionValue);
    if (optionValue === defaultCountry?.code) {
      setFieldValue?.(optionValue);
      onChange?.(optionValue);
    }
  };

  const handleCountrySelectChange = (selectValue) => {
    if (selectValue) {
      setFieldValue?.(selectValue);
      onChange?.(selectValue);
    }
  };

  return (
    <Loader isLoading={isLoading}>
      <FullWidthVStack>
        <SectionTitle>{label}</SectionTitle>

        <Box
          w={responsiveStyle({
            mobile: buttonOptions.length === 1 ? "calc(50% - 0.3125rem)" : "100%",
            desktop: buttonOptions.length === 1 ? "calc(max(10rem, 25%) - 0.3125rem)" : "max(20rem, 50%)",
          })}
        >
          <OptionButtonGroup
            isFormik={false}
            options={buttonOptions}
            value={selectedOption === defaultCountry?.code ? defaultCountry?.code : OTHER_COUNTRY_VALUE}
            onChange={handleCountryOptionChange}
            name={`order-country-options-${name}`}
            lines={[{ length: 2 }]}
          />
        </Box>
        {selectedOption !== defaultCountry?.code && otherCountries?.length > 0 && (
          <Box>
            <Select
              isFormik={false}
              options={selectOptions}
              label={t("order-package-choose_country")}
              value={selectedOption !== OTHER_COUNTRY_VALUE ? selectedOption : undefined}
              onChange={handleCountrySelectChange}
              id={`order-country-select-${name}`}
              mode="fixedOption"
              isNotClearable
              searchWithOnlyBeginnings
            />
          </Box>
        )}
        {fieldError && <FormMessage message={fieldError} isInvalid />}
      </FullWidthVStack>
    </Loader>
  );
};
export default withCompositeFormikField({ hasSetFieldValue: true, hasOnChangeCallback: false })(CountrySelect);
